import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-neutral-50 py-12" data-testid="privacy-policy-page">
      <Helmet>
        <title>Privacy Policy | Explore MHC - Martinsville & Henry County, VA</title>
        <meta name="description" content="Privacy policy for Explore MHC. Learn how we collect, use, and protect your information on our Martinsville and Henry County community platform." />
        <link rel="canonical" href="https://exploremhc.com/privacy-policy" />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-neutral-200">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-neutral-500 mb-8">Last updated: March 6, 2026</p>

          <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">1. Introduction</h2>
              <p>Welcome to Explore MHC ("we," "our," or "us"). We are committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website exploremhc.com (the "Site") and use our services.</p>
              <p>By using the Site, you agree to the collection and use of information in accordance with this policy.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">2. Information We Collect</h2>
              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Personal Information</h3>
              <p>We may collect personally identifiable information that you voluntarily provide when:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Registering for an account (name, email address, phone number, zip code)</li>
                <li>Submitting a business listing (business name, address, contact details, photos)</li>
                <li>Submitting an event (event details, location, contact information)</li>
                <li>Using our contact form (name, email, message)</li>
                <li>Making a payment for featured placement (payment information processed by Stripe)</li>
              </ul>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Automatically Collected Information</h3>
              <p>When you visit the Site, we may automatically collect certain information, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP address and browser type</li>
                <li>Pages visited and time spent on the Site</li>
                <li>Referring website address</li>
                <li>Device information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Create and manage your account</li>
                <li>Display business listings and events on the Site</li>
                <li>Process payments for featured placements</li>
                <li>Send email notifications regarding your submissions and account</li>
                <li>Respond to your inquiries via the contact form</li>
                <li>Improve the Site and our services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">4. Information Sharing</h2>
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Service Providers:</strong> Third-party services that assist in operating our Site (e.g., Stripe for payment processing, Resend for email delivery)</li>
                <li><strong>Public Listings:</strong> Information you submit for business listings or events will be publicly displayed on the Site</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">5. Payment Processing</h2>
              <p>All payment transactions are processed through Stripe. We do not store your credit card information on our servers. Stripe's use of your personal information is governed by their privacy policy at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">stripe.com/privacy</a>.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">6. Data Security</h2>
              <p>We implement reasonable security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">7. Cookies and Tracking</h2>
              <p>We may use cookies and similar tracking technologies to enhance your experience on the Site. You can set your browser to refuse cookies, but some features of the Site may not function properly without them.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">8. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your account and associated data</li>
                <li>Opt out of marketing communications</li>
              </ul>
              <p className="mt-2">To exercise these rights, please contact us at <a href="mailto:hello@exploremhc.com" className="text-primary hover:underline">hello@exploremhc.com</a>.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">9. Children's Privacy</h2>
              <p>The Site is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">10. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">11. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Email: <a href="mailto:hello@exploremhc.com" className="text-primary hover:underline">hello@exploremhc.com</a></li>
                <li>Website: <a href="/contact" className="text-primary hover:underline">exploremhc.com/contact</a></li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
